import * as angular from 'angular';
import { inRange } from 'lodash';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';

import { QueryOptions } from '@mode/shared/data-access-algolia';
import { BugsnagHelpers } from '~/lib/bugsnag-helpers';
import { TableListData } from '@mode/shared/ui-angularjs';
import { TableListSchema } from '@mode/shared/ui-angularjs';
import { TableListValueType } from '@mode/shared/ui-angularjs';
import { SearchClientService } from '@mode/algolia/domain';

// configure QueryOptions and constants
const DEFAULT_PAGE = 0;
const DEFAULT_RESULT_PAGE_SIZE = 10;
type GroupSpacesQueryOptions = QueryOptions<'space'> & {
  rawResults?: true;
};

export class GroupSpacesBrowserController {
  private orgToken: string;
  private groupToken: string;
  private groupSpaces$: BehaviorSubject<TableListData<any>[]> = new BehaviorSubject([]);
  private query$: BehaviorSubject<string> = new BehaviorSubject(null);
  private pageNum$: BehaviorSubject<number> = new BehaviorSubject(DEFAULT_PAGE);
  private currPage$: BehaviorSubject<number> = new BehaviorSubject(null);
  private pageCount$: BehaviorSubject<number> = new BehaviorSubject(0);
  private hitsCount$: BehaviorSubject<number> = new BehaviorSubject(0);
  private groupSpacesTableListSchema: TableListSchema = {
    fields: [
      {
        name: 'name',
        title: 'Collection Name',
        type: TableListValueType.Text,
      },
      {
        name: 'reports',
        title: 'Reports',
        type: TableListValueType.Text,
      },
      {
        name: 'createdBy',
        title: 'Created By',
        type: TableListValueType.Text,
      },
      {
        name: 'createdOn',
        title: 'Created On',
        type: TableListValueType.Text,
      },
    ],
  };
  private isLoading = true;

  static $inject = ['SearchClientService'];

  constructor(private _searchClientService: SearchClientService) {
    const queries$ = this.query$.pipe(filter((query) => query != null));
    combineLatest(queries$, this.pageNum$).subscribe(([query, pageNum]: [string, number]) => {
      this.querySpaces(query, pageNum);
    });
  }

  $onInit() {
    this.updateQuery();
  }

  //
  // Querying to Algolia and parsing its results
  querySpaces(query = '', page = DEFAULT_PAGE) {
    const opts: GroupSpacesQueryOptions = {
      page: page,
      query: query || '',
      indexes: ['space'],
      perPage: DEFAULT_RESULT_PAGE_SIZE,
      rawResults: true,
      filters: `organization_${this.orgToken},user_group_${this.groupToken}`,
    };

    const queryPromise = this._searchClientService.query(opts);

    return queryPromise
      .then((response) => this.handleGroupSpacesSearchResponse(response))
      .catch((err) => BugsnagHelpers.notifySafely(err));
  }

  handleGroupSpacesSearchResponse(response): void {
    const spaceHits = (response) => response.results[0].hits;
    const currPage = (response) => response.results[0].page;
    const pageCount = (response) => response.results[0].nbPages;
    const hitsCount = (response) => response.results[0].nbHits;
    const groupSpacesList = spaceHits(response).map((spaces) => ({
      values: this._searchClientService.createGroupSpaceFromHit(spaces),
    }));
    this.isLoading = false;
    this.groupSpaces$.next(groupSpacesList);
    this.currPage$.next(currPage(response));
    this.pageCount$.next(pageCount(response));
    this.hitsCount$.next(hitsCount(response));
  }

  // Search & Filtering Logic
  handleQueryUpdate($query: string): void {
    this.updateQuery($query);
  }

  updateQuery(query = '') {
    this.resetPagination();
    this.query$.next(query);
  }

  //
  // Pagination Logic
  updatePage(pageNum: number) {
    this.resetPagination();
    this.pageNum$.next(pageNum);
  }

  validPage(page: number, pageRange: number): boolean {
    return inRange(page, 0, pageRange + 1);
  }

  handlePageSelection($page: number) {
    if (this.validPage($page, this.pageCount$.getValue())) {
      this.updatePage($page);
    }
  }

  resetPagination(): void {
    this.currPage$.next(0);
    this.pageNum$.next(0);
  }
}

export const GroupSpacesBrowserComponent: angular.IComponentOptions = {
  controller: GroupSpacesBrowserController,
  controllerAs: 'ctrl',
  bindings: {
    orgToken: '<',
    groupToken: '<',
  },
  template: `
    <browser ng-if="!ctrl.isLoading">
      <browser-header>
        <capra-search-input placeholder="Search..." (text-change)="ctrl.handleQueryUpdate($event)" />
      </browser-header>

      <browser-body>
        <table-list
          ng-if="(ctrl.groupSpaces$ | async:this).length"
          schema="ctrl.groupSpacesTableListSchema"
          data="ctrl.groupSpaces$ | async:this"
          />

        <div ng-if="!(ctrl.groupSpaces$ | async:this).length">
          No Results Found
        </div>
      </browser-body>

      <browser-footer>
        <paginator
          curr-page="ctrl.currPage$ | async:this"
          page-range="ctrl.pageCount$ | async:this"
          inner-window-size="2"
          left-outer-window-size="1"
          right-outer-window-size="1"
          on-page-selection="ctrl.handlePageSelection($pageIndex)"
          num-items-page="(ctrl.groupSpaces$ | async:this).length"
          num-items-total="ctrl.hitsCount$ | async:this" />
      </browser-footer>
    </browser>

    <div ng-if="ctrl.isLoading">
      <mode-loader></mode-loader>
    </div>
  `,
};
